/**
 * Translations for the validation messages baked into the zod schemas.
 *
 * The schemas are shared modules with no React in them, so they cannot call
 * `useT()` — their messages are written in English at definition time, and a
 * Spanish manager filling in a Spanish form was told "Campaign name is
 * required". Rewriting every schema into a factory that takes a translator
 * would touch a lot of code for one line of output each.
 *
 * Instead the message is translated where it is *displayed*, in `FormField`.
 * The lookup is by exact English string, and anything not in this map renders
 * unchanged — so a message added tomorrow shows English rather than a raw key,
 * which is the failure mode you want: unhelpful, but never broken-looking.
 */
const KEY_BY_MESSAGE: Record<string, string> = {
  'Name is required': 'validation.nameRequired',
  'Quantity must be at least 1': 'validation.quantityMin',
  'Company name is required': 'validation.companyNameRequired',
  'Contact name is required': 'validation.contactNameRequired',
  'Please pick an industry': 'validation.industryRequired',
  'Campaign name is required': 'validation.campaignNameRequired',
  'Logo is required': 'validation.logoRequired',
  'Use a HEX color like #B262F5': 'validation.hexColor',
  'Use a HEX color like #1A1A2E': 'validation.hexColor',
  // Alta rediseñada (sept 2026)
  'Full name must be at least 2 characters': 'validation.fullNameMin',
  'You must accept the terms to continue': 'validation.consentRequired',
  'Organization name must be at least 2 characters': 'validation.orgNameMin',
  'Brand name must be at least 2 characters': 'validation.brandNameMin',
  'Please select an industry': 'validation.industrySelect',
  'Please select a company size': 'validation.companySizeSelect',
  // Acceso, alta y recuperación (12 oct 2026). Antes los formularios dejaban
  // validar al navegador, que avisa en el idioma del ordenador y no en el de
  // la app; con `noValidate` estos mensajes son los únicos que se ven.
  'Please enter a valid email address': 'validation.emailInvalid',
  'Password must be at least 6 characters': 'validation.passwordMin6',
  'Password must be at least 8 characters': 'validation.passwordMin8',
  // Onboarding (12 oct 2026): el tipo de cuenta de `brandKit.ts` salía en inglés también en ES.
  'Please pick one': 'validation.pickOne',
};

/**
 * Translate a validation message when we recognise it; otherwise hand it back
 * untouched.
 */
export function translateValidationMessage(
  message: string | undefined,
  t: (key: string) => string,
): string | undefined {
  if (!message) return message;
  const key = KEY_BY_MESSAGE[message];
  return key ? t(key) : message;
}
